import React, { useState, useEffect } from 'react';
import { Menu, X, Globe } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { setSeo } from '../lib/seo';
import { Button } from './ui/button';

const Header = () => {
  const { language, toggleLanguage, t } = useLanguage();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 40);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const pathBySection = { hero: '/', about: '/nosotros', portfolio: '/proyectos', contact: '/contacto' };
    const homePaths = ['/', '/nosotros', '/proyectos', '/contacto'];
    const targetPath = pathBySection[sectionId] || '/';
    // If not on home page, navigate to the section route (HomePage scrolls on mount)
    // Conserva parámetros de atribución (ej. ?oppref= de ChatGPT Ads) al navegar
    const search = window.location.search || '';
    if (!homePaths.includes(location.pathname)) {
      navigate(targetPath + search);
      setMobileMenuOpen(false);
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
        window.history.replaceState(null, '', targetPath + search);
        setSeo(targetPath);
        setMobileMenuOpen(false);
      }
    }
  };

  const handleAgendaClick = () => {
    navigate('/agenda');
    setMobileMenuOpen(false);
  };

  const goTo = (to) => (e) => {
    e.preventDefault();
    navigate(to);
    setMobileMenuOpen(false);
    window.scrollTo(0, 0);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-white/95 backdrop-blur-md border-b border-neutral-200 shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" style={{ textShadow: scrolled ? 'none' : '0 1px 3px rgba(0,0,0,0.6)' }}>
        <div className="flex justify-between items-center h-24">
          {/* Logo */}
          <div className="flex-shrink-0 cursor-pointer" onClick={goTo('/')}>
            <img
              src="/images/logo-cr-home.png"
              alt="CR Home"
              className="h-28 w-auto object-contain transition-all duration-300"
              style={{ filter: scrolled ? 'none' : 'brightness(0) invert(1) drop-shadow(0 1px 3px rgba(0,0,0,0.6))' }}
            />
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-10">
            <a href="/" onClick={goTo('/')} data-testid="nav-inicio" className={`transition-colors duration-200 font-light text-sm tracking-wider hover:text-amber-600 ${
                scrolled ? 'text-neutral-700' : 'text-white'
              }`}>
              {t.nav.home}
            </a>
            <a href="/nosotros" onClick={goTo('/nosotros')} data-testid="nav-nosotros" className={`transition-colors duration-200 font-light text-sm tracking-wider hover:text-amber-600 ${
                scrolled ? 'text-neutral-700' : 'text-white'
              }`}>
              {language === 'es' ? 'Nosotros' : 'About'}
            </a>
            <a href="/proyectos" onClick={goTo('/proyectos')} data-testid="nav-proyectos" className={`transition-colors duration-200 font-light text-sm tracking-wider hover:text-amber-600 ${
                scrolled ? 'text-neutral-700' : 'text-white'
              }`}>
              {language === 'es' ? 'Proyectos' : 'Projects'}
            </a>
            <a href="https://shop.crhome.mx/" target="_blank" rel="noopener" data-testid="nav-tienda-desktop" className={`transition-colors duration-200 font-light text-sm tracking-wider hover:text-amber-600 ${
                scrolled ? 'text-neutral-700' : 'text-white'
              }`}>
              {language === 'es' ? 'Tienda en línea' : 'Online Store'}
            </a>
            <a href="/agenda" onClick={goTo('/agenda')} data-testid="nav-agenda" className={`transition-colors duration-200 font-light text-sm tracking-wider hover:text-amber-600 ${
                scrolled ? 'text-neutral-700' : 'text-white'
              }`}>
              {language === 'es' ? 'Agenda tu cita' : 'Book appointment'}
            </a>
            <a href="/blog" onClick={goTo('/blog')} data-testid="nav-blog" className={`transition-colors duration-200 font-light text-sm tracking-wider hover:text-amber-600 ${
                scrolled ? 'text-neutral-700' : 'text-white'
              }`}>
              Blog
            </a>
            <a href="/contacto" onClick={goTo('/contacto')} data-testid="nav-contacto" className={`transition-colors duration-200 font-light text-sm tracking-wider hover:text-amber-600 ${
                scrolled ? 'text-neutral-700' : 'text-white'
              }`}>
              {t.nav.contact}
            </a>
          </nav>

          {/* Right side buttons */}
          <div className="hidden md:flex items-center space-x-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleLanguage}
              className={`flex items-center gap-2 hover:text-amber-600 ${
                scrolled ? 'text-neutral-700' : 'text-white hover:bg-white/10'
              }`}
            >
              <Globe className="h-4 w-4" />
              <span className="text-xs font-medium tracking-wider">{language.toUpperCase()}</span>
            </Button>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleLanguage}
              className={`flex items-center gap-1 ${
                scrolled ? 'text-neutral-700' : 'text-white'
              }`}
            >
              <Globe className="h-4 w-4" />
              <span className="text-xs">{language.toUpperCase()}</span>
            </Button>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className={`transition-colors ${
                scrolled ? 'text-neutral-700 hover:text-neutral-900' : 'text-white hover:text-amber-400'
              }`}
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-6 border-t border-neutral-200 bg-white rounded-b-lg" style={{ textShadow: 'none' }}>
            <div className="flex flex-col space-y-4">
              <a href="/" onClick={goTo('/')} className="text-left text-neutral-700 hover:text-amber-600 py-2 text-sm font-light">{t.nav.home}</a>
              <a href="/nosotros" onClick={goTo('/nosotros')} className="text-left text-neutral-700 hover:text-amber-600 py-2 text-sm font-light">{language === 'es' ? 'Nosotros' : 'About'}</a>
              <a href="/proyectos" onClick={goTo('/proyectos')} className="text-left text-neutral-700 hover:text-amber-600 py-2 text-sm font-light">{language === 'es' ? 'Proyectos' : 'Projects'}</a>
              <a href="https://shop.crhome.mx/" target="_blank" rel="noopener" data-testid="nav-tienda-mobile" className="text-left text-neutral-700 hover:text-amber-600 py-2 text-sm font-light">{language === 'es' ? 'Tienda en línea' : 'Online Store'}</a>
              <a href="/agenda" onClick={goTo('/agenda')} className="text-left text-neutral-700 hover:text-amber-600 py-2 text-sm font-light">{language === 'es' ? 'Agenda tu cita' : 'Book appointment'}</a>
              <a href="/blog" onClick={goTo('/blog')} className="text-left text-neutral-700 hover:text-amber-600 py-2 text-sm font-light">Blog</a>
              <a href="/contacto" onClick={goTo('/contacto')} className="text-left text-neutral-700 hover:text-amber-600 py-2 text-sm font-light">{t.nav.contact}</a>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;